import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getCategoriasAdmin,
  cambiarEstadoCategoria,
  eliminarCategoria,
} from '../../services/adminService';
import { useInstitucion } from '../../context/InstitucionContext';
import { aplanarCategorias } from '../../utils/categoriasArbol';

const AdminCategorias = () => {
  const { paleta: colors } = useInstitucion();

  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCategoriasAdmin();
      setCategorias(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const arbol = useMemo(() => aplanarCategorias(categorias), [categorias]);

  // Cantidad de subcategorías directas por categoría, para mostrar su rol
  // (contenedora vs hoja con publicaciones).
  const conteoHijas = useMemo(() => {
    const conteo = new Map();
    categorias.forEach((c) => {
      if (c.padreId != null) conteo.set(c.padreId, (conteo.get(c.padreId) || 0) + 1);
    });
    return conteo;
  }, [categorias]);

  const descripcionContenido = (cat) => {
    const hijas = conteoHijas.get(cat.id) || 0;
    if (hijas > 0) return `${hijas} ${hijas === 1 ? 'subcategoría' : 'subcategorías'}`;
    const total = cat.totalPublicaciones || 0;
    return `${total} ${total === 1 ? 'publicación' : 'publicaciones'}`;
  };

  const handleToggleEstado = async (cat) => {
    try {
      await cambiarEstadoCategoria(cat.id, !cat.activo);
      setCategorias((prev) =>
        prev.map((c) => (c.id === cat.id ? { ...c, activo: !c.activo } : c))
      );
    } catch (err) {
      alert(err.message);
    }
  };

  const handleEliminar = async (cat) => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.nombre}"? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await eliminarCategoria(cat.id);
      setCategorias((prev) => prev.filter((c) => c.id !== cat.id));
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorías</h1>
          <p className="text-gray-500 text-sm mt-1">Gestiona las categorías y su jerarquía</p>
        </div>
        <Link
          to="/admin/categorias/nueva"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white font-semibold rounded-lg shadow-md transition-all hover:opacity-90"
          style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Nueva categoría
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Cargando categorías...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-500">{error}</div>
        ) : arbol.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            No hay categorías todavía. Crea la primera con "Nueva categoría".
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-200">
                  <th className="px-4 py-3 font-semibold">Nombre</th>
                  <th className="px-4 py-3 font-semibold hidden md:table-cell">Slug</th>
                  <th className="px-4 py-3 font-semibold hidden sm:table-cell">Contenido</th>
                  <th className="px-4 py-3 font-semibold hidden lg:table-cell">Orden de publicaciones</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  <th className="px-4 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {arbol.map((cat) => (
                  <tr key={cat.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div
                        className="flex items-center gap-2"
                        style={{ paddingLeft: `${cat.nivel * 24}px` }}
                      >
                        {cat.nivel > 0 && (
                          <span className="text-gray-300 select-none" aria-hidden="true">└</span>
                        )}
                        <div>
                          <div className="font-medium text-gray-900">{cat.nombre}</div>
                          {cat.descripcion && (
                            <div className="text-gray-400 text-xs line-clamp-1">{cat.descripcion}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{cat.slug}</td>
                    <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{descripcionContenido(cat)}</td>
                    <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
                      {(conteoHijas.get(cat.id) || 0) > 0
                        ? '—'
                        : cat.tipoOrden === 'manual' ? 'Manual (campo orden)' : 'Por fecha'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleEstado(cat)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          cat.activo
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                        title="Clic para cambiar el estado"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${cat.activo ? 'bg-green-500' : 'bg-gray-400'}`} />
                        {cat.activo ? 'Activa' : 'Inactiva'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/categorias/${cat.id}/editar`}
                          className="text-gray-500 hover:text-gray-900 p-1.5 rounded-md hover:bg-gray-100"
                          title="Editar"
                        >
                          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Link>
                        <button
                          onClick={() => handleEliminar(cat)}
                          className="text-red-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50"
                          title="Eliminar"
                        >
                          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCategorias;
